import React, { useEffect, useState } from 'react';
import { get, post, patch, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, Pencil, Trash2, Shield, KeyRound, Building2, Layers, Activity, RotateCcw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';

const ALL_MODULES = [
  { id: 'dashboard', label: 'Dashboard Executivo' },
  { id: 'consolidado', label: 'Consolidado do Grupo' },
  { id: 'dre', label: 'DRE Gerencial' },
  { id: 'fluxo-caixa', label: 'Fluxo de Caixa' },
  { id: 'lancamentos', label: 'Lançamentos' },
  { id: 'importar', label: 'Importar Fluxo de Caixa' },
  { id: 'cursos', label: 'Cursos & Eventos' },
  { id: 'contratos', label: 'Contratos Recorrentes' },
  { id: 'contas-pagar', label: 'Contas a Pagar' },
  { id: 'cobranca', label: 'Contas a Receber & Cobrança' },
  { id: 'metas', label: 'Meta vs Realizado' },
  { id: 'fechamento', label: 'Fechamento Mensal' },
  { id: 'cadastros', label: 'Cadastros (Empresas, Fontes, Centros, Categorias)' },
];

const blankForm = () => ({
  email: '',
  name: '',
  password: '',
  role: 'RESTRICTED',
  allowed_company_ids: [],
  allowed_modules: [],
  active: true,
});

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());

  const load = async () => {
    try {
      const [us, cos] = await Promise.all([get('/auth/users'), get('/companies')]);
      setUsers(us); setCompanies(cos);
    } catch (e) {
      toast.error('Erro ao carregar usuários');
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blankForm());
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      email: u.email,
      name: u.name,
      password: '',
      role: u.role,
      allowed_company_ids: u.allowed_company_ids || [],
      allowed_modules: u.allowed_modules || [],
      active: u.active,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) return toast.error('Preencha email e nome');
    if (!editing && !form.password) return toast.error('Senha obrigatória ao criar usuário');
    if (form.role === 'RESTRICTED' && form.allowed_company_ids.length === 0)
      return toast.error('Selecione ao menos 1 empresa para usuário restrito');
    if (form.role === 'RESTRICTED' && form.allowed_modules.length === 0)
      return toast.error('Selecione ao menos 1 módulo para usuário restrito');

    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await patch(`/auth/users/${editing}`, payload);
        toast.success('Usuário atualizado');
      } else {
        await post('/auth/users', form);
        toast.success('Usuário criado');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast.error('Você não pode apagar a si mesmo');
    if (!window.confirm(`Remover usuário "${u.name}"?`)) return;
    try {
      await del(`/auth/users/${u.id}`);
      toast.success('Removido');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const toggleCompany = (cid) => {
    const has = form.allowed_company_ids.includes(cid);
    setForm({
      ...form,
      allowed_company_ids: has ? form.allowed_company_ids.filter((x) => x !== cid) : [...form.allowed_company_ids, cid],
    });
  };

  const toggleModule = (mid) => {
    const has = form.allowed_modules.includes(mid);
    setForm({
      ...form,
      allowed_modules: has ? form.allowed_modules.filter((x) => x !== mid) : [...form.allowed_modules, mid],
    });
  };

  const selectAllModules = () => setForm({ ...form, allowed_modules: ALL_MODULES.map((m) => m.id) });
  const clearModules = () => setForm({ ...form, allowed_modules: [] });
  const selectAllCompanies = () => setForm({ ...form, allowed_company_ids: companies.map((c) => c.id) });
  const clearCompanies = () => setForm({ ...form, allowed_company_ids: [] });

  return (
    <div className="fade-in">
      <TopBar title="Usuários & Permissões" subtitle="Gestão de acessos por empresa e módulo (somente Master)" />
      <div className="px-8 py-7">
        <SectionCard
          title="Usuários do Sistema"
          subtitle={`${users.length} usuário${users.length === 1 ? '' : 's'} cadastrado${users.length === 1 ? '' : 's'}`}
          action={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Auditar todos os usuários e remover IDs de empresas que não existem mais no cadastro?\n\nIsso resolve casos onde um usuário vê dados de empresas indevidas devido a IDs órfãos gravados.')) return;
                  try {
                    const r = await post('/auth/users/sanitize-orphans');
                    if (r.users_cleaned === 0) {
                      toast.success('Nenhum ID órfão encontrado — todas as permissões estão consistentes.');
                    } else {
                      toast.success(`${r.users_cleaned} usuário(s) saneados. Lista de removidos no console.`);
                      console.log('Sanitize-orphans details:', r.details);
                      await load();
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erro ao sanear');
                  }
                }}
                className="border-amber-700/40 text-amber-400 hover:bg-amber-950/30 gap-1.5"
                data-testid="btn-sanitize-orphans"
              >
                <Shield size={14} /> Auditar Permissões
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const d = await get('/admin/courses-diagnostic');
                    const lines = [
                      `Total cursos: ${d.total_courses}`,
                      `Total empresas: ${d.total_companies}`,
                      `Órfãos: ${d.orphan_clients_count} clients, ${d.orphan_costs_count} costs, ${d.orphan_entries_count} entries`,
                      '',
                      'CURSOS NO BANCO:',
                      ...d.courses.map(c => `• [${c.company_name}] ${c.name} | clients=${c.course_clients_count}, costs=${c.course_costs_count}, entries=${c.entries_count} | comps=[${c.seen_competences.join(',')}]`),
                    ];
                    console.log('=== Diagnóstico Completo de Cursos ===');
                    console.log(d);
                    alert(lines.join('\n'));
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erro');
                  }
                }}
                className="border-blue-700/40 text-blue-400 hover:bg-blue-950/30 gap-1.5"
                data-testid="btn-courses-diagnostic"
              >
                <Activity size={14} /> Diagnóstico de Cursos
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Criar a empresa "CeL Cursos" (se não existir) e 4 CURSOS PLACEHOLDER em Maio/2026?\n\nCada curso terá:\n  • 1 NF placeholder de R$ 5.000 (PENDENTE)\n  • 1 custo placeholder de R$ 1.500\n\nApós a criação, você ABRE cada curso em /cursos e EDITA com os dados reais (nome, NFs, custos).\n\nSe os 4 cursos já existirem em Maio/2026, nada será duplicado.')) return;
                  try {
                    const r = await post('/admin/seed-cel-cursos-maio');
                    if (r.skipped_reason) {
                      toast.info(`Empresa "${r.company_name}" já tem ${r.existing_courses_count} cursos em Maio/2026 — nada criado.`);
                      console.log('Existing courses:', r.existing_courses);
                      return;
                    }
                    toast.success(
                      `✓ ${r.courses_created} cursos placeholder criados para "${r.company_name}" em Maio/2026. Vá em /cursos para editá-los.`,
                      { duration: 8000 },
                    );
                    console.log('Seed result:', r);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erro ao criar placeholders');
                  }
                }}
                className="border-purple-700/40 text-purple-400 hover:bg-purple-950/30 gap-1.5"
                data-testid="btn-seed-cel-cursos"
              >
                <Sparkles size={14} /> Criar CeL Cursos + 4 Maio
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('RESSUSCITAR cursos órfãos?\n\nIsso recria cursos a partir de course_clients e course_costs que perderam vínculo. Útil quando cursos sumiram da tela /cursos mas os dados (NFs, custos) continuam no banco.\n\nO sistema mostra primeiro um plano (dry_run). Se OK, executa a gravação.')) return;
                  try {
                    const dry = await post('/admin/courses-resurrect-from-orphans', { dry_run: true });
                    if (dry.orphan_groups === 0) {
                      toast.success('Nenhum curso órfão encontrado. Tudo consistente!');
                      return;
                    }
                    const plan = dry.plan || [];
                    const summary = plan.map(p => p.skipped
                      ? `• SKIPPED ${p.orphan_course_id?.slice(0,8)}: ${p.reason}`
                      : `• ${p.inferred_company_name}: ${p.new_course_name} (${p.clients_count} NFs, R$ ${(p.total_revenue || 0).toLocaleString('pt-BR')})`
                    ).join('\n');
                    if (!window.confirm(`PLANO DE RESSURREIÇÃO (${dry.orphan_groups} grupos):\n\n${summary}\n\nConfirma a gravação no banco?`)) return;
                    const real = await post('/admin/courses-resurrect-from-orphans', { dry_run: false });
                    toast.success(`${real.courses_resurrected} curso(s) ressuscitado(s)! Volte para /cursos para ver.`, { duration: 7000 });
                    console.log('Resurrect plan:', real.plan);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erro ao ressuscitar');
                  }
                }}
                className="border-emerald-700/40 text-emerald-400 hover:bg-emerald-950/30 gap-1.5"
                data-testid="btn-resurrect-courses"
              >
                <RotateCcw size={14} /> Ressuscitar Cursos Sumidos
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Limpar Lançamentos antigos de Cursos no Fluxo Realizado?\n\nIsso remove APENAS Entries que foram criadas pelo parser legado de Cursos (que injetava receitas no Fluxo sem confirmação de recebimento). Lançamentos confirmados via "Marcar como Pago" são preservados.\n\nUse se você está vendo receitas duplicadas no Dashboard depois de importar Cursos.')) return;
                  try {
                    const r = await post('/admin/cleanup-course-entries');
                    if (r.deleted_count === 0) {
                      toast.success('Nenhum lançamento órfão de cursos encontrado — Fluxo está consistente.');
                    } else {
                      toast.success(`${r.deleted_count} lançamento(s) removido(s) — total R$ ${r.deleted_total_amount.toLocaleString('pt-BR', {minimumFractionDigits: 2})} liberado do Fluxo Realizado.`, { duration: 6000 });
                      console.log('Cleanup details:', r.deleted_details);
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erro ao limpar');
                  }
                }}
                className="border-red-700/40 text-red-400 hover:bg-red-950/30 gap-1.5"
                data-testid="btn-cleanup-course-entries"
              >
                <Trash2 size={14} /> Limpar Lançamentos Antigos de Cursos
              </Button>
              <Button onClick={openNew} className="btn-gold gap-1.5" data-testid="btn-new-user"><Plus size={14} /> Novo Usuário</Button>
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full ceap-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Tipo</th>
                  <th>Empresas</th>
                  <th>Módulos</th>
                  <th className="text-center">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="text-zinc-100 font-medium">{u.name}{u.id === me?.id && <span className="ml-2 text-[10px] gold-text">(você)</span>}</td>
                    <td className="font-mono text-xs text-zinc-300">{u.email}</td>
                    <td>
                      {u.role === 'MASTER' ? (
                        <span className="pill text-[10px]" style={{ background: '#D4AF3722', color: '#D4AF37', borderColor: '#D4AF3740' }}>
                          <Shield size={10} className="inline mr-1" /> MASTER
                        </span>
                      ) : (
                        <span className="pill text-[10px] pill-warning">RESTRITO</span>
                      )}
                    </td>
                    <td className="text-xs text-zinc-400">
                      {u.role === 'MASTER' ? 'Todas' : `${u.allowed_company_ids?.length || 0} empresa${(u.allowed_company_ids?.length || 0) === 1 ? '' : 's'}`}
                    </td>
                    <td className="text-xs text-zinc-400">
                      {u.role === 'MASTER' ? 'Todos' : `${u.allowed_modules?.length || 0} módulo${(u.allowed_modules?.length || 0) === 1 ? '' : 's'}`}
                    </td>
                    <td className="text-center">
                      <span className={`pill text-[10px] ${u.active ? 'pill-success' : 'pill-danger'}`}>{u.active ? 'ATIVO' : 'INATIVO'}</span>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100" data-testid={`btn-edit-user-${u.id}`}>
                          <Pencil size={13} />
                        </button>
                        {u.id !== me?.id && (
                          <button onClick={() => remove(u)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-delete-user-${u.id}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Users size={18} className="text-amber-400" />
              {editing ? 'Editar Usuário' : 'Novo Usuário'}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {editing ? 'Edite nome, senha, tipo e permissões.' : 'Cadastre um novo usuário com permissões granulares.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome Completo *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Maria Silva" data-testid="user-form-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="maria@ceapbrasil.com" disabled={!!editing} data-testid="user-form-email" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  <KeyRound size={11} className="inline mr-1" /> Senha {editing ? '(deixe em branco para manter)' : '*'}
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="••••••••" data-testid="user-form-password" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="MASTER">Master (acesso a tudo)</SelectItem>
                    <SelectItem value="RESTRICTED">Restrito (limitado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.role === 'RESTRICTED' && (
              <>
                {/* Empresas Permitidas */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                      <Building2 size={11} className="inline mr-1" /> Empresas Permitidas ({form.allowed_company_ids.length}/{companies.length})
                    </Label>
                    <div className="flex gap-2">
                      <button type="button" onClick={selectAllCompanies} className="text-[10px] text-zinc-400 hover:text-amber-400">Marcar todas</button>
                      <span className="text-[10px] text-zinc-700">·</span>
                      <button type="button" onClick={clearCompanies} className="text-[10px] text-zinc-400 hover:text-red-400">Limpar</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 border border-zinc-800 rounded p-3 bg-zinc-900/30 max-h-44 overflow-y-auto" data-testid="user-form-companies">
                    {companies.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-[12px] cursor-pointer hover:bg-zinc-900/60 px-2 py-1 rounded">
                        <input type="checkbox" checked={form.allowed_company_ids.includes(c.id)} onChange={() => toggleCompany(c.id)} className="rounded" data-testid={`user-form-company-${c.id}`} />
                        <span className="text-zinc-200 truncate">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Módulos Permitidos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                      <Layers size={11} className="inline mr-1" /> Módulos Permitidos ({form.allowed_modules.length}/{ALL_MODULES.length})
                    </Label>
                    <div className="flex gap-2">
                      <button type="button" onClick={selectAllModules} className="text-[10px] text-zinc-400 hover:text-amber-400">Marcar todos</button>
                      <span className="text-[10px] text-zinc-700">·</span>
                      <button type="button" onClick={clearModules} className="text-[10px] text-zinc-400 hover:text-red-400">Limpar</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 border border-zinc-800 rounded p-3 bg-zinc-900/30 max-h-56 overflow-y-auto" data-testid="user-form-modules">
                    {ALL_MODULES.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 text-[12px] cursor-pointer hover:bg-zinc-900/60 px-2 py-1 rounded">
                        <input type="checkbox" checked={form.allowed_modules.includes(m.id)} onChange={() => toggleModule(m.id)} className="rounded" data-testid={`user-form-module-${m.id}`} />
                        <span className="text-zinc-200 truncate">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="rounded" data-testid="user-form-active" />
              Usuário ativo (desmarque para bloquear acesso)
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold gap-1.5" data-testid="btn-save-user">
              {editing ? 'Salvar Alterações' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
